import axios from "axios";
import config from '../config';
import { tokenStore } from "../auth/tokenStore";
import { exchangeRefreshToken } from "../auth/vioSession";

// Create axios instance
const api = axios.create({
  baseURL: config.apiBaseUrl,
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
});

let refreshPromise = null;
let authFailureHandler = null;

const hasTokenExpiredMessage = (payload) => {
  const message = String(
    payload?.message || payload?.error?.message || payload?.error || ""
  ).toLowerCase();
  return message.includes("token expired") || message.includes("expired token");
};

const isLegalAcceptanceError = (status, payload) =>
  status === 403 &&
  (payload?.errorCode === "LEGAL_ACCEPTANCE_REQUIRED" ||
    payload?.code === "LEGAL_ACCEPTANCE_REQUIRED" ||
    payload?.error?.code === "LEGAL_ACCEPTANCE_REQUIRED");

// reason: "signin" (no usable VIO session) or "legal" (terms pending in VIO).
const notifyAuthFailure = (reason = "signin") => {
  if (typeof authFailureHandler === "function") {
    try {
      authFailureHandler(reason);
    } catch (error) {
      console.error("❌ Auth failure callback error:", error);
    }
  }
};

export { isLegalAcceptanceError };

export const setAuthFailureHandler = (handler) => {
  authFailureHandler = handler;
};

/**
 * New access token from the stored refresh token, via the backend
 * (GET /auth/token). Single-flight: concurrent 401s share one exchange.
 * A transient failure (network / VIO down) keeps the session; a rejected
 * refresh token ends it.
 */
const getRefreshedAccessToken = async () => {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      const refreshToken = tokenStore.getRefreshToken();
      if (!refreshToken) {
        const error = new Error("Failed to refresh access token");
        error.kind = "signin";
        throw error;
      }
      const accessToken = await exchangeRefreshToken(refreshToken);
      tokenStore.setAccessToken(accessToken);
      return accessToken;
    })();
  }

  try {
    return await refreshPromise;
  } catch (error) {
    if (error.kind !== "transient") {
      tokenStore.clearTokens();
      notifyAuthFailure("signin");
    }
    throw new Error("Failed to refresh access token");
  } finally {
    refreshPromise = null;
  }
};

// Request interceptor to add auth token
api.interceptors.request.use(
  (requestConfig) => {
    const accessToken = tokenStore.getAccessToken();
    if (accessToken) {
      requestConfig.headers.Authorization = `Bearer ${accessToken}`;
    }
    return requestConfig;
  },
  (error) => Promise.reject(error)
);

// Response interceptor with token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Pending VIO terms: the token is fine, refreshing would not help.
    if (isLegalAcceptanceError(error.response?.status, error.response?.data)) {
      notifyAuthFailure("legal");
      return Promise.reject(error);
    }

    const shouldRefresh =
      originalRequest &&
      !originalRequest._retry &&
      (error.response?.status === 401 || hasTokenExpiredMessage(error.response?.data));

    // If auth token is invalid and we haven't retried, refresh once then retry
    if (shouldRefresh) {
      originalRequest._retry = true;
      try {
        const newAccessToken = await getRefreshedAccessToken();
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Identity comes from the VIO token on every request; no user id is sent.

// Rewards API
export const rewardsAPI = {
  // Process shake reward
  processShake: async (shakeData) => {
    // The backend validates the token, claims stock and sends the prize to VIO
    // (up to ~15 s for the send alone). The default 10 s timeout reported
    // "failed" for shakes that were still being granted.
    const response = await api.post("/rewards/shake", shakeData, { timeout: 40000 });
    return response.data;
  },

  // Get user rewards. The list has no paging UI; the backend default (20) hid
  // older rewards.
  getUserRewards: async (params = {}) => {
    const response = await api.get("/rewards", { params: { limit: 100, ...params } });
    return response.data;
  },

  // Get user's shake balance (single source of truth).
  // Returns: { balance, totalGained, totalUsed, canShake, dailyLoginBonus }
  getBalance: async () => {
    const response = await api.get("/rewards/balance");
    return response.data;
  },

  // Check daily login status
  getDailyLoginStatus: async () => {
    const response = await api.get("/rewards/daily-login-status");
    return response.data;
  },

  // Grant daily login bonus
  grantDailyLogin: async () => {
    const response = await api.post("/rewards/daily-login", {});
    return response.data;
  },
};

// Program API
export const programAPI = {
  // Get active program (member-safe fields only)
  getActiveProgram: async () => {
    const response = await api.get("/programs/active");
    return response.data;
  },
};

// Entries API
export const entriesAPI = {
  // Get user entries
  getUserEntries: async (params = {}) => {
    const response = await api.get("/entries", { params });
    return response.data;
  },
};

export default api;
